'use client'

import classNames from 'classnames'
import React, { useEffect, useRef } from 'react'
import { ProductTypeProps } from '.'
import AddToCartButton from '../add-to-cart-button'

import { DigitalDataOrigin, ProductWithCategoryType } from '../utils/digital-data-types'
import { useTrackingOnScreen } from '@dustin-web/microsite-components/src/on-screen/'
import { Card } from './product-card-gallery-composable'
import { ProductDigitalData } from '../utils/product-digital-data'
import { getProductLabels } from './utils'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'
import { getRefVar, lastIndexVar } from '@dustin-web/microsite-components/src/hooks/vars'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

type Props = {
  imagePriority?: boolean
  product?: ProductTypeProps | null
  ticket: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex?: number
  sizeClasses?: string
  oneRowLayout?: boolean
  hTag?: HeadingProps['hTag']
}

const ProductCardCompact = ({
  product,
  ticket,
  imagePriority = false,
  digitalDataOrigin,
  digitalIndex = 0,
  sizeClasses,
  hTag,
  oneRowLayout,
}: Props) => {
  const { variationKey } = useFeatureFlag('ab-test-recently-viewed-add-to-cart')

  if (!product) {
    return null
  }

  return (
    <Container
      sizeClasses={sizeClasses}
      digitalIndex={digitalIndex}
      product={product}
      digitalDataOrigin={digitalDataOrigin}>
      <Card.Picture
        className="u-mb-0"
        product={product}
        ticket={ticket}
        imageWidth={64}
        imageHeight={48}
        imagePriority={imagePriority}
        digitalDataIndex={digitalIndex}
        digitalDataOrigin={digitalDataOrigin}
      />
      <div
        className={`c-product-card__product-info ${
          product.availability?.isAvailableForSale ? 'u-mr-12' : ''
        }`}>
        <div className="d-flex flex-column flex-fill u-w-100">
          <div className="flex-fill">
            <Card.Title
              product={product}
              ticket={ticket}
              digitalDataIndex={digitalIndex}
              digitalDataOrigin={digitalDataOrigin}
              hTag={hTag}
              compact
              ellipsis
            />
          </div>
          {!oneRowLayout && (
            <Card.Price
              className="u-pt-12 gap-8"
              price={product.price}
              isAvailableForSale={product.availability?.isAvailableForSale}
              displayVatInfo={false}
            />
          )}
        </div>
      </div>
      {oneRowLayout && (
        <Card.Price
          price={product.price}
          isAvailableForSale={product.availability?.isAvailableForSale}
          displayVatInfo={false}
        />
      )}

      {product.availability?.isAvailableForSale && (
        <AddToCartButton
          buttonClassName={classNames('u-w-40', {
            secondary: variationKey === 'whitebutton',
          })}
          mainProductErpIdentifier={null}
          product={product}
          ticket={ticket}
          isAd={false}
          quantity={1}
          digitalDataOrigin={digitalDataOrigin}
          digitalDataIndex={digitalIndex}
          primary={variationKey !== 'whitebutton'}
          showMiniBtn
        />
      )}
    </Container>
  )
}

type ContainerProps = {
  children: React.ReactNode
  sizeClasses: Props['sizeClasses']
  digitalIndex: number
  product: Props['product']
  digitalDataOrigin: Props['digitalDataOrigin']
}

function pushViewItemList(
  digitalIndex?: number,
  digitalDataOrigin?: DigitalDataOrigin,
  product?: ProductWithCategoryType | ProductTypeProps | null
) {
  if (product && digitalDataOrigin && digitalIndex !== undefined) {
    ProductDigitalData.pushViewItemList(
      [
        {
          ...product,
          productListIndex: digitalIndex,
          label: getProductLabels(product as ProductTypeProps),
        },
      ] as ProductWithCategoryType[],
      digitalDataOrigin
    )
  }
}

const Container = ({
  children,
  sizeClasses,
  digitalIndex,
  product,
  digitalDataOrigin,
}: ContainerProps) => {
  const ref = useTrackingOnScreen(() => pushViewItemList(digitalIndex, digitalDataOrigin, product))

  const indexRef = useRef<HTMLDivElement | null>(null)
  const storedIndex = lastIndexVar()

  useEffect(() => {
    if (digitalIndex && storedIndex !== null && storedIndex === digitalIndex) {
      if (indexRef.current) {
        getRefVar(indexRef)
      }
    }
  }, [storedIndex])

  return (
    <div
      id={`compactCardContainer_${digitalIndex}`}
      ref={ref}
      className={classNames(
        sizeClasses,
        'c-product-card c-product-card--compact c-product-card--compact-v2 t-nullify u-overflow-hidden gap-12'
      )}>
      <div style={{ display: 'none' }} ref={indexRef}></div>
      {children}
    </div>
  )
}

export default ProductCardCompact
